import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Button, DialogActions, Menu, Paper, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setSorting } from '@/redux/slices/dataTableSlice';
import { selectDataTableDefaultSorting, selectDataTableSorting } from '@/redux/selectors/dataTableSelectors';

import { SortingObject } from '@/__configs/config_types';
import { useToast } from '@/components/_common/Toast';
import useColumns from '@/components/_hooks/useColumns';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import MultiSortList from '@/components/_common/Table/AdvanceSort/AdvanceSortList';
import ResetButton from '@/components/_common/IconButtons/ResetButton';
import { DndContext, DragOverEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

type AdvanceSortMenuProps = {
  isOpen: boolean;
  anchorEl: null | HTMLElement;
  onClose: () => void;
};

const setSortFromState = (currentSortFields: string[] | SortingObject) => {
  if (!Array.isArray(currentSortFields) && typeof currentSortFields === 'object' && !!currentSortFields?.id) {
    return [currentSortFields] as SortingObject[];
  }

  if (!currentSortFields || (currentSortFields as string[]).length === 0) {
    return [{ id: '', order: 'asc' }] as SortingObject[];
  }

  return (currentSortFields as string[]).map((sortField) => ({
    id: sortField.charAt(0) === '-' ? sortField.substring(sortField.indexOf('-') + 1) : sortField,
    order: sortField.charAt(0) === '-' ? 'desc' : 'asc'
  })) as SortingObject[];
};

function AdvanceSortMenu({ isOpen, anchorEl, onClose }: AdvanceSortMenuProps) {
  const { error } = useToast();
  const { advanceSortColumns } = useColumns();
  const dispatch = useAppDispatch();
  const currentSortFields = useAppSelector(selectDataTableSorting);
  const defaultSortFields = useAppSelector(selectDataTableDefaultSorting);

  const [sortFields, setSortFields] = useState<SortingObject[]>(setSortFromState(currentSortFields as string[] | SortingObject));

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!!active.id && !!over?.id) {
      const oldIndex = sortFields.findIndex((card) => card.id === active.id);
      const newIndex = sortFields.findIndex((card) => card.id === over.id);
      const newOrder = arrayMove(sortFields, oldIndex, newIndex);
      setSortFields(newOrder);
    }
  };

  const handleAddFieldClick = () => {
    if (sortFields.length === advanceSortColumns.length) {
      return error('Maximum sort fields reached');
    }

    setSortFields((prevSortFields) => [...prevSortFields, { id: '', order: 'asc' }]);
  };

  const handleSortingChange = (index: number, id: SortingObject['id'], order: SortingObject['order']) => {
    setSortFields((prevSortFields) => {
      const newSortFields = [...prevSortFields];
      newSortFields[index] = { id, order };
      return newSortFields;
    });
  };

  const handleRemoveSort = (index: number) => {
    if (sortFields.length === 1) return;
    setSortFields((prevSortFields) => prevSortFields.filter((_, i) => i !== index));
  };

  const handleApplyClick = () => {
    const isSortFieldsNotComplete = sortFields.some((sortField) => !sortField.id);
    if (isSortFieldsNotComplete) {
      return error('Please complete the Sort form');
    }

    const newSorting = sortFields.map((sortField) => `${sortField.order === 'desc' ? '-' : ''}${sortField.id}`);
    dispatch(setSorting(newSorting));
    onClose();
  };

  const resetSort = () => {
    dispatch(setSorting(defaultSortFields));
    onClose();
  };

  useEffect(() => {
    setSortFields(setSortFromState(currentSortFields as string[]));
  }, [currentSortFields]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  return (
    <StyledMenu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
    >
      <StyledMenuPaper>
        <TitleSection>
          <HeaderIcon>
            <PolarisIcon icon="swap" />
          </HeaderIcon>
          <ModalTitle variant="h2">Advanced Sort</ModalTitle>
        </TitleSection>
        <DndContext sensors={sensors} onDragOver={onDragOver}>
          <SortableContext strategy={verticalListSortingStrategy} items={sortFields.map((field) => field.id)}>
            <MultiSortList sortFields={sortFields} onSortingChange={handleSortingChange} onRemoveSort={handleRemoveSort} />
          </SortableContext>
        </DndContext>
        {sortFields.length < 3 && (
          <AddFieldButton variant="text" startIcon={<PolarisIcon icon="add" />} onClick={handleAddFieldClick}>
            Add Field
          </AddFieldButton>
        )}
        <ButtonRow>
          <ResetButton onClick={resetSort} />
          <Actions>
            <Button color="default" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleApplyClick}>
              Apply
            </Button>
          </Actions>
        </ButtonRow>
      </StyledMenuPaper>
    </StyledMenu>
  );
}

export default AdvanceSortMenu;

const TitleSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  column-gap: ${({ theme }) => theme.spacing(1)};
  border-bottom: ${({ theme }) => `1px solid ${theme.palette.divider}`};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

const HeaderIcon = styled.span`
  background: ${({ theme }) => theme.palette.primary.main + '3a'};
  margin-right: ${({ theme }) => theme.spacing(1)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.primary.main};
  width: ${({ theme }) => theme.spacing(4)};
  height: ${({ theme }) => theme.spacing(4)};
  border-radius: 50%;
  padding: ${({ theme }) => theme.spacing(0.75)};
  margin-left: ${({ theme }) => theme.spacing(-1)};
`;

const ModalTitle = styled(Typography)`
  font-size: 1.1rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightNormal};

  & > strong {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const StyledMenu = styled(Menu)(({ theme }) => ({
  '.MuiPaper-root.MuiMenu-paper': {
    backgroundColor: theme.palette.background.paper,
    opacity: 1,
    border: 0,
    '> ul': {
      paddingTop: 0,
      paddingBottom: 0
    }
  }
}));

const StyledMenuPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(1),
  minWidth: 500,
  maxWidth: '100%',
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  backgroundColor: theme.palette.background.paper
}));

const AddFieldButton = styled(Button)(({ theme }) => ({
  width: 'fit-content',
  marginTop: theme.spacing(1),
  '& .MuiButton-icon svg': {
    fontSize: theme.typography.fontSize
  }
}));

const ButtonRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

const Actions = styled(DialogActions)(({ theme }) => ({
  columnGap: theme.spacing(1)
}));
